import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-kit";
import { Loader, EmptyState } from "@/components/Loader";
import { Users, Timer, Gauge, ShieldAlert, MapPin, Clock, ChevronRight, BarChart3, Radio, HeartPulse } from "lucide-react";

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const SEV = { Low: "#6b7280", Medium: "#F59E0B", High: "#F97316", Critical: "#DC2626" };

export default function CommunityLive() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  useEffect(() => {
    api.get("/community/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
    api.get("/community/health").then((r) => setHealth(r.data)).catch(() => setHealth(null));
  }, []);
  if (!data) return <Loader />;

  return (
    <div className="ll-fade-up" data-testid="community-live-screen">
      <PageHeader title="Community pulse" subtitle={`${data.community_name} · ${data.status} community`} />
      <div className="px-5">
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[{ icon: Users, v: data.residents_helping_today, l: "Helping today", c: "#7c3aed" },
            { icon: Timer, v: `${data.avg_response_sec}s`, l: "Avg response", c: "#F97316" },
            { icon: Gauge, v: `${data.readiness_score}%`, l: "Readiness", c: "#16A34A" }].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-white rounded-2xl p-4 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#111827]/5">
              <s.icon className="w-5 h-5 mx-auto mb-1.5" style={{ color: s.c }} />
              <p className="text-xl font-black font-head">{s.v}</p>
              <p className="text-[10px] text-[#111827]/50 font-semibold">{s.l}</p>
            </motion.div>
          ))}
        </div>

        <Card className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-bold font-head">Community readiness</h3>
            <span className="font-black text-[#16A34A]">{data.readiness_score}%</span>
          </div>
          <div className="h-3 rounded-full bg-[#111827]/10 overflow-hidden">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-[#F97316] to-[#16A34A]" initial={{ width: 0 }} animate={{ width: `${data.readiness_score}%` }} transition={{ duration: 1 }} />
          </div>
          <p className="text-sm text-[#111827]/50 mt-2">{data.demo_responders} verified responders active in {data.community_name}.</p>
        </Card>

        {health && (
          <Card className="mb-4" data-testid="community-health">
            <div className="flex items-center gap-2 mb-4"><HeartPulse className="w-5 h-5 text-[#DC2626]" /><h3 className="text-lg font-bold font-head">Community health</h3></div>
            <div className="grid grid-cols-2 gap-3">
              {[{ l: "Verification rate", v: `${health.verification_rate}%`, s: `${health.verified_members}/${health.total_members} members` },
                { l: "Active guardians", v: health.active_guardians, s: "high-trust responders" },
                { l: "Avg response", v: `${health.avg_response_sec}s`, s: "first responder" },
                { l: "Readiness", v: `${health.readiness_score}%`, s: "overall score" }].map((m, i) => (
                <div key={i} className="bg-[#FFF7E6] rounded-2xl p-4">
                  <p className="text-2xl font-black font-head">{m.v}</p>
                  <p className="text-xs font-bold text-[#111827]/70">{m.l}</p>
                  <p className="text-[11px] text-[#111827]/45">{m.s}</p>
                </div>
              ))}
            </div>
            {(health.open_reports > 0 || health.pending_verifications > 0) && (
              <p className="text-xs text-[#92400e] bg-[#F59E0B]/12 rounded-lg px-3 py-2 mt-3">{health.open_reports} open report(s) · {health.pending_verifications} pending verification(s) awaiting admin review.</p>
            )}
          </Card>
        )}

        <div className="flex items-center justify-between mb-3 px-1">
          <h3 className="text-lg font-bold font-head flex items-center gap-2"><Radio className="w-5 h-5 text-[#DC2626]" /> Active emergencies</h3>
          <span className="text-[11px] font-bold uppercase tracking-wider bg-[#0E7C66]/10 text-[#0E7C66] px-2 py-0.5 rounded-full">Live</span>
        </div>
        {(data.active_emergencies || []).length === 0 ? (
          <EmptyState icon={ShieldAlert} title="All quiet right now" subtitle="No active emergencies in your community." />
        ) : (
          <div className="space-y-3">
            {data.active_emergencies.map((e) => (
              <button key={e.id} data-testid={`active-emg-${e.id}`} onClick={() => navigate(`/emergency/${e.id}`)} className="w-full bg-white rounded-2xl p-4 text-left shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#111827]/5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-white px-2.5 py-0.5 rounded-full" style={{ background: SEV[e.severity] }}>{e.severity}</span>
                  {e.is_demo && <span className="text-[10px] font-bold bg-[#C4B5FD]/40 text-[#4c1d95] px-2 py-0.5 rounded-full">Demo</span>}
                </div>
                <p className="font-bold">{e.type_label}</p>
                <div className="flex items-center justify-between mt-1 text-sm text-[#111827]/50">
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location_label}</span>
                  <span className="flex items-center gap-1 text-[#F97316] font-semibold">View <ChevronRight className="w-3.5 h-3.5" /></span>
                </div>
              </button>
            ))}
          </div>
        )}

        <button onClick={() => navigate("/analytics")} data-testid="community-analytics" className="w-full mt-5 bg-[#111827] text-white rounded-2xl py-4 font-bold flex items-center justify-center gap-2"><BarChart3 className="w-5 h-5" /> View analytics</button>
      </div>
    </div>
  );
}
